import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, parseJsonSafe, handleUnauthorized, formatDate, formatEur } from '../utils/api';

const STATUS_OPTIONS = [
  { value: 'nuevo', label: 'Nuevo', color: 'primary' },
  { value: 'interesa', label: 'Interesa', color: 'success' },
  { value: 'contactado', label: 'Contactado', color: 'info' },
  { value: 'descartado', label: 'Descartado', color: 'secondary' }
];
const statusMeta = (value) => STATUS_OPTIONS.find((s) => s.value === value) || { label: value || '-', color: 'light' };

const RUN_STATUS_LABEL = {
  en_curso: 'En curso',
  ok: 'Correcta',
  parcial: 'Parcial',
  sin_publicacion: 'Sin boletín',
  pendiente: 'Pendiente (aún no publicado)',
  error: 'Error'
};
const RUN_STATUS_BADGE = {
  en_curso: 'bg-warning text-dark',
  ok: 'bg-success',
  parcial: 'bg-warning text-dark',
  sin_publicacion: 'bg-secondary',
  pendiente: 'bg-info text-dark',
  error: 'bg-danger'
};

const DEFAULT_FILTERS = {
  search: '',
  province: '',
  city: '',
  legal_form: '',
  status: '',
  date_from: '',
  date_to: '',
  capital_min: '',
  capital_max: '',
  cnae: '',
  has_lead: ''
};

const truncate = (text, max = 110) => {
  if (!text) return '-';
  const str = String(text);
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

const cargosToText = (cargos) => {
  if (!Array.isArray(cargos) || !cargos.length) return '';
  return cargos.map((c) => `${c.cargo}: ${c.nombre}`).join(' · ');
};

const CaptacionBormePage = () => {
  const token = localStorage.getItem('token');
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftSearch, setDraftSearch] = useState('');
  const [sort, setSort] = useState({ key: 'borme_date', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [legalForms, setLegalForms] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailNotes, setDetailNotes] = useState('');
  const [savingDetail, setSavingDetail] = useState(false);
  const [creatingLeadId, setCreatingLeadId] = useState(null);

  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ cron_enabled: true, cron_time: '09:30', catchup_days: 7, provinces: '' });
  const [configModal, setConfigModal] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [runs, setRuns] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncDate, setSyncDate] = useState('');

  // ---------- Carga de datos ----------

  const request = useCallback(async (path, options = {}) => {
    const res = await apiFetch(path, { ...options, headers: { ...authHeaders, ...(options.headers || {}) } });
    if (res.status === 401) {
      handleUnauthorized();
      throw new Error('Sesión expirada');
    }
    return res;
  }, [authHeaders]);

  const buildParams = useCallback(({ includePagination = true } = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) params.append(key, value);
    });
    params.append('sort', sort.key);
    params.append('dir', sort.dir);
    if (includePagination) {
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));
    }
    return params;
  }, [filters, sort, page, pageSize]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await request(`/api/borme/companies?${buildParams().toString()}`);
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error cargando empresas');
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
      setSelected([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [request, buildParams]);

  const fetchAux = useCallback(async () => {
    try {
      const [provRes, formRes, statsRes] = await Promise.all([
        request('/api/borme/companies/provinces'),
        request('/api/borme/companies/legal-forms'),
        request('/api/borme/companies/stats')
      ]);
      if (provRes.ok) setProvinces(await parseJsonSafe(provRes));
      if (formRes.ok) setLegalForms(await parseJsonSafe(formRes));
      if (statsRes.ok) setStats(await parseJsonSafe(statsRes));
    } catch (err) {
      console.error('Error cargando auxiliares BORME', err);
    }
  }, [request]);

  const fetchConfig = useCallback(async () => {
    try {
      const [cfgRes, runsRes] = await Promise.all([request('/api/borme/config'), request('/api/borme/runs?limit=30')]);
      if (cfgRes.ok) {
        const data = await parseJsonSafe(cfgRes);
        setConfig(data);
        setConfigForm({
          cron_enabled: data.settings?.cron_enabled !== false,
          cron_time: data.settings?.cron_time || '09:30',
          catchup_days: data.settings?.catchup_days || 7,
          provinces: (data.settings?.provinces || []).join(', ')
        });
      }
      if (runsRes.ok) setRuns(await parseJsonSafe(runsRes));
    } catch (err) {
      console.error('Error cargando configuración BORME', err);
    }
  }, [request]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); fetchConfig(); }, [fetchAux, fetchConfig]);

  // Búsqueda de texto con pequeño retardo para no disparar una petición por tecla
  useEffect(() => {
    const t = setTimeout(() => {
      if (draftSearch !== filters.search) {
        setPage(1);
        setFilters((p) => ({ ...p, search: draftSearch }));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [draftSearch, filters.search]);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((p) => ({ ...p, [key]: value }));
  };

  const resetFilters = () => {
    setDraftSearch('');
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const toggleSort = (key) => {
    setPage(1);
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' || key === 'province' || key === 'city' ? 'asc' : 'desc' }));
  };

  const sortIndicator = (key) => {
    if (sort.key !== key) return <span className="text-muted ms-1" style={{ fontSize: 10 }}>↕</span>;
    return <span className="ms-1">{sort.dir === 'asc' ? '▲' : '▼'}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;

  // ---------- Acciones ----------

  const runSync = async () => {
    setSyncing(true);
    setError('');
    setNotice('');
    try {
      const res = await request('/api/borme/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(syncDate ? { date: syncDate } : {})
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo sincronizar');
      const runsDone = data.runs || [];
      const fresh = runsDone.reduce((acc, r) => acc + Number(r.companies_new || 0), 0);
      const found = runsDone.reduce((acc, r) => acc + Number(r.companies_found || 0), 0);
      if (!runsDone.length) {
        setNotice('No había días pendientes de sincronizar.');
      } else {
        const detailTxt = runsDone.map((r) => `${formatDate(r.borme_date)}: ${RUN_STATUS_LABEL[r.status] || r.status}`).join(' · ');
        setNotice(`Sincronización terminada. ${fresh} empresas nuevas (${found} constituciones leídas). ${detailTxt}`);
      }
      await Promise.all([fetchList(), fetchAux(), fetchConfig()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await request('/api/borme/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cron_enabled: !!configForm.cron_enabled,
          cron_time: configForm.cron_time,
          catchup_days: Number(configForm.catchup_days) || 7,
          provinces: configForm.provinces
        })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setNotice('Configuración guardada.');
      await fetchConfig();
      setConfigModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await request(`/api/borme/companies/${id}`);
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar la empresa');
      setDetail(data);
      setDetailNotes(data.notes || '');
    } catch (err) {
      setError(err.message);
    }
  };

  const patchCompany = async (id, body) => {
    const res = await request(`/api/borme/companies/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await parseJsonSafe(res);
    if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
    return data;
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await patchCompany(id, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: updated.status } : it)));
      if (detail && detail.id === id) setDetail((d) => ({ ...d, status: updated.status }));
      fetchAux();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveDetail = async () => {
    if (!detail) return;
    setSavingDetail(true);
    try {
      const updated = await patchCompany(detail.id, { notes: detailNotes });
      setDetail((d) => ({ ...d, notes: updated.notes }));
      setItems((prev) => prev.map((it) => (it.id === detail.id ? { ...it, notes: updated.notes } : it)));
      setNotice('Notas guardadas.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDetail(false);
    }
  };

  const createLead = async (company) => {
    setCreatingLeadId(company.id);
    setError('');
    try {
      const res = await request(`/api/borme/companies/${company.id}/create-lead`, { method: 'POST' });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo crear el lead');
      setItems((prev) => prev.map((it) => (it.id === company.id ? { ...it, client_id: data.clientId, status: it.status === 'nuevo' ? 'interesa' : it.status } : it)));
      if (detail && detail.id === company.id) setDetail((d) => ({ ...d, client_id: data.clientId }));
      setNotice(data.status === 'existing'
        ? `Ya existía un cliente/lead con esa razón social; se ha enlazado (ID ${data.clientId}).`
        : `Lead creado en Clientes (ID ${data.clientId}).`);
      fetchAux();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingLeadId(null);
    }
  };

  const bulkStatus = async (status) => {
    if (!selected.length) return;
    try {
      const res = await request('/api/borme/companies', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selected, status })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      setNotice(`${data.updated} empresas marcadas como «${statusMeta(status).label}».`);
      fetchList();
      fetchAux();
    } catch (err) {
      setError(err.message);
    }
  };

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`¿Eliminar ${selected.length} empresas del listado? Volverán a importarse si se vuelve a sincronizar ese día.`)) return;
    try {
      const res = await request('/api/borme/companies', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selected })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      setNotice(`${data.deleted} empresas eliminadas.`);
      fetchList();
      fetchAux();
    } catch (err) {
      setError(err.message);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await request(`/api/borme/companies/export?${buildParams({ includePagination: false }).toString()}`);
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error(data.message || 'Error exportando');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'borme_empresas.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleSelectAll = (checked) => setSelected(checked ? items.map((i) => i.id) : []);
  const toggleSelect = (id, checked) => setSelected((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));

  const lastRun = config?.last_run;

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h2 className="mb-1">Captación Borme</h2>
          <div className="text-muted small">
            Empresas de nueva constitución publicadas cada día en el Boletín Oficial del Registro Mercantil (sección «Actos inscritos»).
            Se sincroniza automáticamente de lunes a viernes.
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 160 }}
            value={syncDate}
            onChange={(e) => setSyncDate(e.target.value)}
            title="Día concreto a sincronizar (vacío = días pendientes)"
          />
          <button className="btn btn-primary btn-sm" onClick={runSync} disabled={syncing || config?.running}>
            {syncing || config?.running ? 'Sincronizando…' : (syncDate ? 'Sincronizar día' : 'Sincronizar pendientes')}
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfigModal(true)}>Configuración</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2" role="alert">{error} <button type="button" className="btn-close float-end" aria-label="Cerrar" onClick={() => setError('')} /></div>}
      {notice && <div className="alert alert-success py-2" role="alert">{notice} <button type="button" className="btn-close float-end" aria-label="Cerrar" onClick={() => setNotice('')} /></div>}

      <div className="row g-2 mb-3">
        <div className="col-6 col-md-2">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted small">Empresas</div>
            <div className="fs-4 fw-semibold">{stats ? stats.total : '-'}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted small">Último boletín {stats?.ultima_fecha ? `(${formatDate(stats.ultima_fecha)})` : ''}</div>
            <div className="fs-4 fw-semibold">{stats ? stats.ultima_fecha_total : '-'}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted small">Últimos 7 días</div>
            <div className="fs-4 fw-semibold">{stats ? stats.ultimos_7_dias : '-'}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted small">Sin revisar</div>
            <div className="fs-4 fw-semibold">{stats ? stats.nuevos : '-'}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted small">Convertidas en lead</div>
            <div className="fs-4 fw-semibold">{stats ? stats.con_lead : '-'}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card shadow-sm h-100"><div className="card-body py-2">
            <div className="text-muted small">Última sincronización</div>
            {lastRun ? (
              <div>
                <span className={`badge ${RUN_STATUS_BADGE[lastRun.status] || 'bg-light text-dark'}`}>{RUN_STATUS_LABEL[lastRun.status] || lastRun.status}</span>
                <div className="small text-muted">{formatDateTime(lastRun.finished_at || lastRun.started_at)} · boletín {formatDate(lastRun.borme_date)}</div>
              </div>
            ) : <div className="small text-muted">Nunca</div>}
            {config?.pending_dates?.length > 0 && (
              <div className="small text-warning">{config.pending_dates.length} día(s) pendientes</div>
            )}
          </div></div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 mb-2">
            <div className="col-md-3 col-sm-6">
              <input className="form-control form-control-sm" placeholder="Buscar empresa, objeto social, administrador, dirección…" value={draftSearch} onChange={(e) => setDraftSearch(e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6">
              <select className="form-select form-select-sm" value={filters.province} onChange={(e) => updateFilter('province', e.target.value)}>
                <option value="">Provincia (todas)</option>
                {provinces.map((p) => <option key={p.province} value={p.province}>{p.province} ({p.total})</option>)}
              </select>
            </div>
            <div className="col-md-2 col-sm-6">
              <input className="form-control form-control-sm" placeholder="Localidad" value={filters.city} onChange={(e) => updateFilter('city', e.target.value)} />
            </div>
            <div className="col-md-1 col-sm-6">
              <select className="form-select form-select-sm" value={filters.legal_form} onChange={(e) => updateFilter('legal_form', e.target.value)}>
                <option value="">Forma</option>
                {legalForms.map((f) => <option key={f.legal_form} value={f.legal_form}>{f.legal_form}</option>)}
              </select>
            </div>
            <div className="col-md-2 col-sm-6">
              <select className="form-select form-select-sm" value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
                <option value="">Estado (todos)</option>
                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-2 col-sm-6">
              <select className="form-select form-select-sm" value={filters.has_lead} onChange={(e) => updateFilter('has_lead', e.target.value)}>
                <option value="">Lead (todas)</option>
                <option value="true">Con lead creado</option>
                <option value="false">Sin lead</option>
              </select>
            </div>
          </div>
          <div className="row g-2 mb-3 align-items-center">
            <div className="col-md-2 col-sm-6">
              <input type="date" className="form-control form-control-sm" value={filters.date_from} onChange={(e) => updateFilter('date_from', e.target.value)} title="Fecha BORME desde" />
            </div>
            <div className="col-md-2 col-sm-6">
              <input type="date" className="form-control form-control-sm" value={filters.date_to} onChange={(e) => updateFilter('date_to', e.target.value)} title="Fecha BORME hasta" />
            </div>
            <div className="col-md-2 col-sm-6">
              <input type="number" min="0" className="form-control form-control-sm" placeholder="Capital mín. €" value={filters.capital_min} onChange={(e) => updateFilter('capital_min', e.target.value)} />
            </div>
            <div className="col-md-2 col-sm-6">
              <input type="number" min="0" className="form-control form-control-sm" placeholder="Capital máx. €" value={filters.capital_max} onChange={(e) => updateFilter('capital_max', e.target.value)} />
            </div>
            <div className="col-md-1 col-sm-6">
              <input className="form-control form-control-sm" placeholder="CNAE" maxLength={4} value={filters.cnae} onChange={(e) => updateFilter('cnae', e.target.value.replace(/\D/g, ''))} title="Código CNAE (4 dígitos), solo cuando el Registro lo publica" />
            </div>
            <div className="col-md-3 col-sm-6 d-flex gap-2 justify-content-md-end">
              {activeFilterCount > 0 && <button className="btn btn-link btn-sm" onClick={resetFilters}>Limpiar filtros</button>}
              <button className="btn btn-outline-secondary btn-sm" onClick={exportCsv}>Exportar CSV</button>
            </div>
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
            <div className="text-muted small">{loading ? 'Cargando…' : `${total} empresas`}{activeFilterCount > 0 && !loading ? ' (filtradas)' : ''}</div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {selected.length > 0 && (
                <>
                  <span className="small text-muted">Seleccionadas: {selected.length}</span>
                  <div className="btn-group btn-group-sm">
                    {STATUS_OPTIONS.map((s) => (
                      <button key={s.value} className="btn btn-outline-secondary" onClick={() => bulkStatus(s.value)}>{s.label}</button>
                    ))}
                  </div>
                  <button className="btn btn-outline-danger btn-sm" onClick={bulkDelete}>Eliminar</button>
                </>
              )}
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>
                    <input type="checkbox" checked={items.length > 0 && selected.length === items.length} onChange={(e) => toggleSelectAll(e.target.checked)} />
                  </th>
                  <th role="button" onClick={() => toggleSort('borme_date')} style={{ whiteSpace: 'nowrap' }}>Fecha BORME{sortIndicator('borme_date')}</th>
                  <th role="button" onClick={() => toggleSort('name')}>Empresa{sortIndicator('name')}</th>
                  <th role="button" onClick={() => toggleSort('legal_form')}>Forma{sortIndicator('legal_form')}</th>
                  <th role="button" onClick={() => toggleSort('province')}>Provincia{sortIndicator('province')}</th>
                  <th role="button" onClick={() => toggleSort('city')}>Localidad{sortIndicator('city')}</th>
                  <th role="button" onClick={() => toggleSort('capital')} className="text-end" style={{ whiteSpace: 'nowrap' }}>Capital{sortIndicator('capital')}</th>
                  <th>Objeto social</th>
                  <th>Administración</th>
                  <th role="button" onClick={() => toggleSort('status')}>Estado{sortIndicator('status')}</th>
                  <th style={{ width: 150 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      {total === 0 && activeFilterCount === 0
                        ? 'Todavía no hay empresas. Pulsa «Sincronizar pendientes» para importar los últimos boletines.'
                        : 'Sin resultados con esos filtros.'}
                    </td>
                  </tr>
                )}
                {items.map((c) => {
                  const st = statusMeta(c.status);
                  return (
                    <tr key={c.id}>
                      <td><input type="checkbox" checked={selected.includes(c.id)} onChange={(e) => toggleSelect(c.id, e.target.checked)} /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(c.borme_date)}</td>
                      <td>
                        <button type="button" className="btn btn-link btn-sm p-0 text-start fw-semibold" onClick={() => openDetail(c.id)}>{c.name}</button>
                        {c.client_id && (
                          <Link to={`/clientes/${c.client_id}`} className="badge bg-success text-decoration-none ms-2" title="Ver lead en Clientes">Lead</Link>
                        )}
                        {c.website && (
                          <div className="small"><a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer">{truncate(c.website, 40)}</a></div>
                        )}
                      </td>
                      <td>{c.legal_form || '-'}</td>
                      <td>{c.province || '-'}</td>
                      <td>{c.city || '-'}</td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{formatEur(c.capital)}</td>
                      <td className="small" title={c.objeto_social || ''} style={{ maxWidth: 320 }}>
                        {truncate(c.objeto_social, 120)}
                        {Array.isArray(c.cnae_codes) && c.cnae_codes.length > 0 && (
                          <div>{c.cnae_codes.map((code) => <span key={code} className="badge bg-light text-dark border me-1">CNAE {code}</span>)}</div>
                        )}
                      </td>
                      <td className="small" title={cargosToText(c.cargos)} style={{ maxWidth: 220 }}>
                        {truncate(cargosToText(c.cargos), 90) || '-'}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          style={{ minWidth: 120, borderLeft: `4px solid var(--bs-${st.color})` }}
                          value={c.status}
                          onChange={(e) => changeStatus(c.id, e.target.value)}
                        >
                          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => openDetail(c.id)} title="Ver ficha">Ver</button>
                          {!c.client_id && (
                            <button className="btn btn-outline-success btn-sm" disabled={creatingLeadId === c.id} onClick={() => createLead(c)} title="Crear lead en Clientes">
                              {creatingLeadId === c.id ? '…' : 'Lead'}
                            </button>
                          )}
                          {c.pdf_url && <a className="btn btn-outline-secondary btn-sm" href={c.pdf_url} target="_blank" rel="noreferrer" title="Boletín oficial (PDF)">PDF</a>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="small text-muted">Página {page} de {totalPages} · {total} empresas</div>
            <div className="d-flex gap-2 align-items-center">
              <select className="form-select form-select-sm" style={{ width: 100 }} value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / pág</option>)}
              </select>
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          </div>
          <div className="text-muted mt-3" style={{ fontSize: 11 }}>
            Fuente de los datos: <a href="https://www.boe.es/datosabiertos/" target="_blank" rel="noreferrer">Agencia Estatal Boletín Oficial del Estado</a> (BORME, sección primera, actos inscritos).
            Los nombres de administradores y socios son datos personales publicados en el boletín: úsalos conforme al RGPD.
          </div>
        </div>
      </div>

      {detail && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={() => setDetail(null)}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">{detail.name}</h5>
                  <div className="small text-muted">
                    BORME {formatDate(detail.borme_date)} · {detail.province} · entrada {detail.entry_number}
                  </div>
                </div>
                <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setDetail(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="text-muted small">Forma jurídica</div>
                    <div>{detail.legal_form || '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small">Comienzo de operaciones</div>
                    <div>{detail.start_date ? formatDate(detail.start_date) : '-'}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small">Capital social</div>
                    <div>{formatEur(detail.capital)}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small">Estado de seguimiento</div>
                    <select className="form-select form-select-sm" value={detail.status} onChange={(e) => changeStatus(detail.id, e.target.value)}>
                      {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <div className="text-muted small">Domicilio</div>
                    <div>{detail.domicilio || '-'}{detail.city ? ` · ${detail.city}` : ''}</div>
                  </div>
                  <div className="col-12">
                    <div className="text-muted small">Objeto social {Array.isArray(detail.cnae_codes) && detail.cnae_codes.length > 0 ? `(CNAE ${detail.cnae_codes.join(', ')})` : ''}</div>
                    <div>{detail.objeto_social || '-'}</div>
                  </div>
                  <div className="col-12">
                    <div className="text-muted small">Órgano de administración y otros cargos</div>
                    {Array.isArray(detail.cargos) && detail.cargos.length ? (
                      <ul className="mb-0">
                        {detail.cargos.map((c, idx) => <li key={`${c.cargo}-${idx}`}><strong>{c.cargo}:</strong> {c.nombre}</li>)}
                      </ul>
                    ) : <div>-</div>}
                  </div>
                  {detail.socio_unico && (
                    <div className="col-12">
                      <div className="text-muted small">Socio único</div>
                      <div>{detail.socio_unico}</div>
                    </div>
                  )}
                  {detail.website && (
                    <div className="col-12">
                      <div className="text-muted small">Página web</div>
                      <a href={detail.website.startsWith('http') ? detail.website : `https://${detail.website}`} target="_blank" rel="noreferrer">{detail.website}</a>
                    </div>
                  )}
                  {detail.otros_conceptos && (
                    <div className="col-12">
                      <div className="text-muted small">Otros conceptos</div>
                      <div>{detail.otros_conceptos}</div>
                    </div>
                  )}
                  <div className="col-12">
                    <div className="text-muted small">Datos registrales</div>
                    <div>{detail.datos_registrales || '-'}</div>
                  </div>
                  <div className="col-12">
                    <label className="form-label text-muted small mb-1" htmlFor="bormeNotes">Notas internas</label>
                    <textarea id="bormeNotes" className="form-control form-control-sm" rows={3} value={detailNotes} onChange={(e) => setDetailNotes(e.target.value)} />
                  </div>
                  <div className="col-12">
                    <details>
                      <summary className="small text-muted">Texto íntegro publicado</summary>
                      <div className="small mt-1" style={{ whiteSpace: 'pre-wrap' }}>{detail.raw_text}</div>
                    </details>
                  </div>
                </div>
              </div>
              <div className="modal-footer justify-content-between">
                <div className="d-flex gap-2">
                  {detail.pdf_url && <a className="btn btn-outline-secondary btn-sm" href={detail.pdf_url} target="_blank" rel="noreferrer">PDF oficial</a>}
                  {detail.html_url && <a className="btn btn-outline-secondary btn-sm" href={detail.html_url} target="_blank" rel="noreferrer">Ver en boe.es</a>}
                </div>
                <div className="d-flex gap-2">
                  {detail.client_id ? (
                    <Link className="btn btn-success btn-sm" to={`/clientes/${detail.client_id}`}>Ver lead{detail.client_name ? `: ${truncate(detail.client_name, 30)}` : ''}</Link>
                  ) : (
                    <button className="btn btn-success btn-sm" disabled={creatingLeadId === detail.id} onClick={() => createLead(detail)}>
                      {creatingLeadId === detail.id ? 'Creando…' : 'Crear lead'}
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" disabled={savingDetail} onClick={saveDetail}>{savingDetail ? 'Guardando…' : 'Guardar notas'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {configModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={() => setConfigModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configuración de Captación Borme</h5>
                <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setConfigModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="form-check form-switch mt-4">
                      <input className="form-check-input" type="checkbox" id="bormeCronEnabled" checked={!!configForm.cron_enabled} onChange={(e) => setConfigForm((p) => ({ ...p, cron_enabled: e.target.checked }))} />
                      <label className="form-check-label" htmlFor="bormeCronEnabled">Sincronización diaria automática</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" htmlFor="bormeCronTime">Hora (Madrid, lunes a viernes)</label>
                    <input id="bormeCronTime" type="time" className="form-control" value={configForm.cron_time} onChange={(e) => setConfigForm((p) => ({ ...p, cron_time: e.target.value }))} />
                    <div className="form-text">El BORME suele publicarse hacia las 9:00. Si a esa hora aún no está, se reintenta cada 2 horas.</div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label" htmlFor="bormeCatchup">Días hacia atrás a recuperar</label>
                    <input id="bormeCatchup" type="number" min="1" max="60" className="form-control" value={configForm.catchup_days} onChange={(e) => setConfigForm((p) => ({ ...p, catchup_days: e.target.value }))} />
                    <div className="form-text">Boletines pendientes que se importan si el servidor estuvo parado.</div>
                  </div>
                  <div className="col-12">
                    <label className="form-label" htmlFor="bormeProvinces">Provincias a importar (vacío = toda España)</label>
                    <input id="bormeProvinces" className="form-control" placeholder="Ej.: MADRID, BARCELONA, 46" value={configForm.provinces} onChange={(e) => setConfigForm((p) => ({ ...p, provinces: e.target.value }))} />
                    <div className="form-text">Nombres de provincia tal y como aparecen en el BORME o códigos INE de dos dígitos, separados por comas.</div>
                  </div>
                </div>

                <h6 className="mt-4">Historial de sincronizaciones</h6>
                <div className="table-responsive" style={{ maxHeight: 280 }}>
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Boletín</th>
                        <th>Ejecutado</th>
                        <th>Origen</th>
                        <th>Resultado</th>
                        <th className="text-end">Provincias</th>
                        <th className="text-end">Constituciones</th>
                        <th className="text-end">Nuevas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.length === 0 && <tr><td colSpan="7" className="text-muted">Sin ejecuciones todavía.</td></tr>}
                      {runs.map((r) => (
                        <tr key={r.id} title={r.error_text || ''}>
                          <td>{formatDate(r.borme_date)}</td>
                          <td className="small">{formatDateTime(r.finished_at || r.started_at)}</td>
                          <td className="small">{r.trigger_type === 'cron' ? 'Automática' : 'Manual'}</td>
                          <td>
                            <span className={`badge ${RUN_STATUS_BADGE[r.status] || 'bg-light text-dark'}`}>{RUN_STATUS_LABEL[r.status] || r.status}</span>
                            {r.error_text && <div className="small text-danger" style={{ maxWidth: 260 }}>{truncate(r.error_text, 120)}</div>}
                          </td>
                          <td className="text-end">{r.provinces_ok}/{r.provinces_total}</td>
                          <td className="text-end">{r.companies_found}</td>
                          <td className="text-end">{r.companies_new}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setConfigModal(false)}>Cerrar</button>
                <button className="btn btn-primary" disabled={savingConfig} onClick={saveConfig}>{savingConfig ? 'Guardando…' : 'Guardar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaptacionBormePage;
